import { useState, useEffect, useCallback } from 'react';

const OFFLINE_DB_NAME = 'lpe-offline-db';
const OFFLINE_STORE_NAME = 'pending-actions';

// Ouvrir IndexedDB
function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(OFFLINE_DB_NAME, 1);
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
    
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      if (!db.objectStoreNames.contains(OFFLINE_STORE_NAME)) {
        db.createObjectStore(OFFLINE_STORE_NAME, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
}

// Ajouter une action en attente
async function addPendingAction(action) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.add({
      ...action,
      timestamp: Date.now()
    });
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);
  });
}

// Récupérer les actions en attente
async function getPendingActions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_STORE_NAME, 'readonly');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.getAll();
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result || []);
  });
}

// Nettoyer les actions synchronisées
async function clearPendingActions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(OFFLINE_STORE_NAME, 'readwrite');
    const store = transaction.objectStore(OFFLINE_STORE_NAME);
    const request = store.clear();
    
    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve();
  });
}

export function useOfflineMode() {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [pendingCount, setPendingCount] = useState(0);
  const [isSyncing, setIsSyncing] = useState(false);

  // Écouter les changements de connexion
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Tenter la synchronisation automatique
      syncData();
    };
    
    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Charger le nombre d'actions en attente
    loadPendingCount();

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Charger le nombre d'actions en attente
  const loadPendingCount = async () => {
    try {
      const actions = await getPendingActions();
      setPendingCount(actions.length);
    } catch (error) {
      console.error('Erreur chargement actions en attente:', error);
    }
  };

  // Enregistrer un tampon hors ligne
  const saveStampOffline = useCallback(async (cardId, boxIndex, value, vendorCode) => {
    try {
      await addPendingAction({
        type: 'stamp',
        cardId,
        boxIndex,
        value,
        vendorCode
      });
      await loadPendingCount();
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde tampon hors ligne:', error);
      return false;
    }
  }, []);

  // Enregistrer un nouveau client hors ligne
  const saveCustomerOffline = useCallback(async (name, phone, pin, referrerCode) => {
    try {
      await addPendingAction({
        type: 'customer',
        name,
        phone,
        pin,
        referrerCode,
        createdAt: new Date().toISOString()
      });
      await loadPendingCount();
      return true;
    } catch (error) {
      console.error('Erreur sauvegarde client hors ligne:', error);
      return false;
    }
  }, []);

  // Synchroniser les données
  const syncData = useCallback(async () => {
    if (!isOnline || isSyncing) return { success: false, message: 'Non disponible' };
    
    setIsSyncing(true);
    try {
      const pendingActions = await getPendingActions();
      
      if (pendingActions.length === 0) {
        setIsSyncing(false);
        return { success: true, message: 'Rien à synchroniser' };
      }

      const stamps = pendingActions.filter(a => a.type === 'stamp');
      const newCustomers = pendingActions.filter(a => a.type === 'customer');

      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/sync/offline`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stamps, newCustomers })
      });

      if (response.ok) {
        const result = await response.json();
        await clearPendingActions();
        setPendingCount(0);
        setIsSyncing(false);
        return { 
          success: true, 
          message: `Synchronisé: ${result.stamps_processed} tampons, ${result.customers_created} clients`,
          data: result
        };
      } else {
        throw new Error('Erreur de synchronisation');
      }
    } catch (error) {
      console.error('Erreur synchronisation:', error);
      setIsSyncing(false);
      return { success: false, message: error.message };
    }
  }, [isOnline, isSyncing]);

  return {
    isOnline,
    pendingCount,
    isSyncing,
    saveStampOffline,
    saveCustomerOffline,
    syncData,
    refreshPendingCount: loadPendingCount
  };
}

export default useOfflineMode;
